import { FilterOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Checkbox,
  Col,
  Drawer,
  Form,
  Grid,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import React from "react";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import {
  LISTING_FEATURE_KEYS,
  LISTING_FEATURE_LABEL_KEYS,
} from "~/types/listing";
import type { UnitListFilters } from "~/types/unit";
import { getFilterLocations } from "~/utils/listingFilterUrl";

const BED_BATH_OPTIONS = ["1+", "2+", "3+"] as const;

export type ListingFilterDrawerProps = {
  open: boolean;
  onClose: () => void;
  filters: UnitListFilters;
  onApply: (next: UnitListFilters) => void;
  applyLoading?: boolean;
};

/**
 * Advanced listing filters: beds/baths and unit features
 * (same feature set as property-mgmt unit/plan forms).
 * Location is set from the home search bar; property is never a tenant filter.
 */
export function ListingFilterDrawer({
  open,
  onClose,
  filters,
  onApply,
  applyLoading = false,
}: ListingFilterDrawerProps) {
  const { t } = useAppTranslation();
  const [form] = Form.useForm<UnitListFilters>();
  const screens = Grid.useBreakpoint();

  React.useEffect(() => {
    if (open) {
      form.setFieldsValue({
        numBedroom: filters.numBedroom,
        numBathroom: filters.numBathroom,
        inUnitLaundry: filters.inUnitLaundry === true,
        dishwasher: filters.dishwasher === true,
        airConditioning: filters.airConditioning === true,
        furnished: filters.furnished === true,
        balcony: filters.balcony === true,
        petFriendly: filters.petFriendly === true,
      });
    }
  }, [open, filters, form]);

  const drawerWidth = screens.md ? 420 : "100%";

  const handleClear = () => {
    form.resetFields();
    const preserved = getFilterLocations(filters);
    onApply(preserved.length ? { locations: preserved } : {});
    onClose();
  };

  const handleFinish = (values: Record<string, unknown>) => {
    const next: UnitListFilters = {};
    const preserved = getFilterLocations(filters);
    if (preserved.length) {
      next.locations = preserved;
    }
    if (typeof values.numBedroom === "string" && values.numBedroom) {
      next.numBedroom = values.numBedroom;
    }
    if (typeof values.numBathroom === "string" && values.numBathroom) {
      next.numBathroom = values.numBathroom;
    }
    for (const key of LISTING_FEATURE_KEYS) {
      if (values[key] === true) {
        next[key] = true;
      }
    }
    onApply(next);
    onClose();
  };

  return (
    <Drawer
      title={
        <Space>
          <FilterOutlined />
          <Typography.Text strong>{t("Filters")}</Typography.Text>
        </Space>
      }
      placement="right"
      width={drawerWidth}
      styles={{
        wrapper: { maxWidth: "100vw" },
      }}
      open={open}
      onClose={onClose}
      destroyOnClose={false}
      footer={
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <Button onClick={handleClear} disabled={applyLoading}>
            {t("Clear_filters")}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            form="listing-browse-filter-form"
            loading={applyLoading}
          >
            {t("Apply_filters")}
          </Button>
        </div>
      }
    >
      <Form
        id="listing-browse-filter-form"
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={false}
      >
        <Form.Item label={t("Number_of_bedrooms")} name="numBedroom">
          <Select
            allowClear
            placeholder={t("Filter_placeholder_bed_bath_minimum")}
            options={BED_BATH_OPTIONS.map((o) => ({ value: o, label: o }))}
          />
        </Form.Item>

        <Form.Item label={t("Number_of_bathrooms")} name="numBathroom">
          <Select
            allowClear
            placeholder={t("Filter_placeholder_bed_bath_minimum")}
            options={BED_BATH_OPTIONS.map((o) => ({ value: o, label: o }))}
          />
        </Form.Item>

        <Form.Item label={t("Listing_features_filter_label")} style={{ marginBottom: 0 }}>
          <Row gutter={[16, 8]}>
            {LISTING_FEATURE_KEYS.map((key) => (
              <Col key={key} xs={12} sm={12}>
                <Form.Item
                  name={key}
                  valuePropName="checked"
                  style={{ marginBottom: 8 }}
                >
                  <Checkbox>{t(LISTING_FEATURE_LABEL_KEYS[key])}</Checkbox>
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export function ListingFilterTriggerButton({
  activeCount,
  onOpen,
  size,
}: {
  activeCount: number;
  onOpen: () => void;
  size?: "small" | "middle" | "large";
}) {
  const { t } = useAppTranslation();
  return (
    <Badge count={activeCount} size="small" offset={[-2, 2]} showZero={false}>
      <Button
        type="default"
        size={size}
        icon={<FilterOutlined />}
        onClick={onOpen}
      >
        {t("Filters")}
      </Button>
    </Badge>
  );
}
